'use client';

import React, { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { useGetEducationSuggestionsInfiniteQuery } from 'src/api/redux';
// Import directly from hook file to avoid circular dependency via hooks/index.ts
import { useGenericComponent } from '../../hooks/useGenericComponent';
import {
  GenericSelectorModal,
  GenericNodeComponent,
  ConnectorType,
} from '../shared';
import {
  EducationalInstitutionItem,
  educationalInstitutionModalConfig,
  educationalInstitutionNodeConfig,
  transformEducationalInstitution,
} from './config';

interface EducationalInstitutionsComponentProps {
  componentId: string;
  initialEducationalInstitutions?: EducationalInstitutionItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

// Extract IDs for Redux storage — always an array of string entity ids
const getItemIds = (items: EducationalInstitutionItem[]) =>
  items.map((i) => String(i.id));

const EducationalInstitutionsComponent: React.FC<
  EducationalInstitutionsComponentProps
> = ({
  componentId,
  initialEducationalInstitutions = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Normalize hydration items (ui_metadata is [{ id, name }]) to the full item shape
  const normalizedInitialItems = useMemo(
    () =>
      initialEducationalInstitutions.map((item) =>
        item?.displayLabel ? item : transformEducationalInstitution(item)
      ),
    [initialEducationalInstitutions]
  );

  const {
    items: institutions,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<EducationalInstitutionItem>({
    componentId,
    type: 'educational_institution',
    reduxDataField: 'value',
    getItemIds,
    initialItems: normalizedInitialItems,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Debounce search term
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch search results from the educational institutions suggestions API
  const { data: searchData, isLoading } =
    useGetEducationSuggestionsInfiniteQuery(
      { q: debouncedSearch },
      { skip: !debouncedSearch || debouncedSearch.length < 2 }
    );

  // Flatten paginated results and transform to standard format
  const searchResults: EducationalInstitutionItem[] = useMemo(() => {
    const results = searchData?.pages?.flatMap((p) => p.results ?? []) ?? [];
    return results.map(transformEducationalInstitution);
  }, [searchData]);

  return (
    <>
      {institutions.length > 0 ? (
        <GenericNodeComponent
          config={educationalInstitutionNodeConfig}
          items={institutions}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-full cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => setShowModal(true)}
          aria-label="Select educational institutions"
          data-testid="educational-institutions-select-button"
        >
          <span className="text-amber-600 text-xs font-medium">
            Select institutions...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={educationalInstitutionModalConfig}
        isOpen={showModal}
        selectedItems={institutions}
        connector={connector}
        isEditing={institutions.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default EducationalInstitutionsComponent;
